import {
  type AssetClassLevel,
  FinancialProductStrategy,
} from '@generated/graphql';
import { ASSET_CLASS_FIELD_BY_LEVEL } from '@interfaces/financialProduct';
import { round } from 'lodash-es';

export type PortfolioPercentageSource = {
  percentage?: number | null;
  effectivePercentage?: number | null;
  financialProduct?: { id?: string | null };
};

/** Child + model fields needed for portfolio-wide % (add/edit, review, IPS). */
export type ModelPortfolioChildForPortfolioPercent =
  PortfolioPercentageSource & {
    assetClass?: { id?: string | null } | null;
    financialProduct?: PortfolioPercentageSource['financialProduct'] & {
      primaryAssetClass?: { id?: string | null };
      secondaryAssetClass?: { id?: string | null };
      tertiaryAssetClass?: { id?: string | null };
    };
  };

export type ModelForPortfolioPercent = {
  strategy?: FinancialProductStrategy | string | null;
  assetClassLevel?: AssetClassLevel | string | null;
  assetClassBranches?: Array<{
    targetPercentage?: number | null;
    assetClass?: { id?: string | null };
  }> | null;
};

export const isAssetClassGroupedModel = (
  model: ModelForPortfolioPercent | null | undefined,
): boolean => model?.strategy === FinancialProductStrategy.AssetClass;

/**
 * Which asset class group a holding belongs to — stored sleeve first, then the
 * product tag at the model's level (same order as product-service apply/read).
 */
export const groupedAssetClassIdForChild = (
  child: ModelPortfolioChildForPortfolioPercent,
  assetClassLevel?: AssetClassLevel | string | null,
): string | undefined => {
  const assetClassField =
    ASSET_CLASS_FIELD_BY_LEVEL[assetClassLevel as AssetClassLevel] ??
    ASSET_CLASS_FIELD_BY_LEVEL.PRIMARY;

  return (
    child.assetClass?.id ??
    child.financialProduct?.[assetClassField]?.id ??
    undefined
  );
};

export const classTargetPercentageForModelChild = (
  child: ModelPortfolioChildForPortfolioPercent,
  model: ModelForPortfolioPercent | null | undefined,
): number | null => {
  if (!isAssetClassGroupedModel(model)) {
    return null;
  }

  const assetClassId = groupedAssetClassIdForChild(
    child,
    model?.assetClassLevel,
  );
  if (!assetClassId) {
    return null;
  }

  const branch = model?.assetClassBranches?.find(
    (entry) => entry.assetClass?.id === assetClassId,
  );
  const target = branch?.targetPercentage;

  return target != null && Number.isFinite(target) ? target : null;
};

/**
 * Portfolio-wide % for a model child — single entry for add/edit table, review
 * allocations, and the same inputs product-service uses for IPS effectivePercentage.
 */
export const resolvePortfolioPercentageForModelChild = (
  child: ModelPortfolioChildForPortfolioPercent,
  model: ModelForPortfolioPercent | null | undefined,
  withinClassOverride?: number | null,
  allowEffectiveFallback = true,
): number | null => {
  if (!isAssetClassGroupedModel(model)) {
    const withinClass = withinClassOverride ?? child.percentage;
    if (withinClass != null && Number.isFinite(withinClass)) {
      return withinClass;
    }

    return allowEffectiveFallback ? (child.effectivePercentage ?? null) : null;
  }

  return resolvePortfolioPercentage(
    child,
    classTargetPercentageForModelChild(child, model),
    withinClassOverride,
    allowEffectiveFallback,
  );
};

/** Mirrors product-service `computeEffectivePercentage` for grouped asset-class rows. */
export const computePortfolioPercentage = (
  withinClassPercentage: number | null | undefined,
  classTargetPercentage: number | null | undefined,
): number | null => {
  if (
    withinClassPercentage == null ||
    classTargetPercentage == null ||
    !Number.isFinite(withinClassPercentage) ||
    !Number.isFinite(classTargetPercentage)
  ) {
    return null;
  }

  return round((withinClassPercentage / 100) * classTargetPercentage, 2);
};

export type PortfolioPercentageReconcileRow = {
  withinClassPercentage: number | null | undefined;
  classTargetPercentage: number | null | undefined;
  groupKey: string | null | undefined;
};

/**
 * {@link computePortfolioPercentage} for a whole list, rounded per group so each
 * group's 2dp rows sum to its rounded exact total. The residual goes to the
 * largest within-class row in the group (first in list order on ties).
 *
 * Mirrors product-service `reconcileGroupedEffectivePercentages` and the
 * holding-service expectedPercentage write, so the portfolio % shown here is
 * the target the household is traded to. Rows with no group or class target
 * return null.
 */
export const reconcilePortfolioPercentages = (
  rows: PortfolioPercentageReconcileRow[],
): Array<number | null> => {
  const scored = rows.map((row) => {
    const { withinClassPercentage, classTargetPercentage, groupKey } = row;
    if (
      groupKey == null ||
      withinClassPercentage == null ||
      classTargetPercentage == null ||
      !Number.isFinite(withinClassPercentage) ||
      !Number.isFinite(classTargetPercentage)
    ) {
      return null;
    }
    const exact = (withinClassPercentage / 100) * classTargetPercentage;
    return {
      groupKey,
      withinClassPercentage,
      exact,
      rounded: round(exact, 2),
    };
  });

  const recipientByGroup = new Map<string, number>();
  const residualByGroup = new Map<string, number>();
  const groupKeys = new Set(
    scored.flatMap((row) => (row ? [row.groupKey] : [])),
  );
  groupKeys.forEach((groupKey) => {
    let exactSum = 0;
    let roundedSum = 0;
    let recipient = -1;
    scored.forEach((row, index) => {
      if (row?.groupKey !== groupKey) return;
      exactSum += row.exact;
      roundedSum += row.rounded;
      if (
        recipient < 0 ||
        row.withinClassPercentage > scored[recipient]!.withinClassPercentage
      ) {
        recipient = index;
      }
    });
    recipientByGroup.set(groupKey, recipient);
    residualByGroup.set(groupKey, round(round(exactSum, 2) - roundedSum, 2));
  });

  return scored.map((row, index) => {
    if (!row) return null;
    if (recipientByGroup.get(row.groupKey) !== index) return row.rounded;
    return Math.max(
      0,
      round(row.rounded + (residualByGroup.get(row.groupKey) ?? 0), 2),
    );
  });
};

/** Group-reconciled portfolio % keyed by product id, for the grouped securities table. */
export const buildReconciledPortfolioShareByProductId = (
  groupedByAssetClass?: Array<{
    securities: PortfolioPercentageSource[];
    branch: { targetPercentage?: number | null };
  }>,
): Map<string, number> => {
  const entries = (groupedByAssetClass ?? []).flatMap(
    ({ branch, securities }, groupIndex) =>
      securities.map((security) => ({
        productId: security.financialProduct?.id,
        row: {
          withinClassPercentage: security.percentage,
          classTargetPercentage: branch.targetPercentage,
          groupKey: `${groupIndex}`,
        },
      })),
  );
  const reconciled = reconcilePortfolioPercentages(
    entries.map(({ row }) => row),
  );

  const map = new Map<string, number>();
  entries.forEach(({ productId }, index) => {
    const value = reconciled[index];
    if (productId && value != null && !map.has(productId)) {
      map.set(productId, value);
    }
  });
  return map;
};

/** Group-reconciled portfolio % keyed by product id for a model's children. */
export const reconciledPortfolioPercentagesForModel = (
  children: ModelPortfolioChildForPortfolioPercent[] | null | undefined,
  model: ModelForPortfolioPercent | null | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!isAssetClassGroupedModel(model) || !children?.length) {
    return map;
  }

  const reconciled = reconcilePortfolioPercentages(
    children.map((child) => ({
      withinClassPercentage: child.percentage,
      classTargetPercentage: classTargetPercentageForModelChild(child, model),
      groupKey: groupedAssetClassIdForChild(child, model?.assetClassLevel),
    })),
  );
  children.forEach((child, index) => {
    const productId = child.financialProduct?.id;
    const value = reconciled[index];
    if (productId && value != null && !map.has(productId)) {
      map.set(productId, value);
    }
  });
  return map;
};

/** Inverse of {@link computePortfolioPercentage} for portfolio-% entry. */
export const computeWithinClassPercentage = (
  portfolioShare: number | null | undefined,
  classTargetPercentage: number | null | undefined,
): number | null => {
  if (
    portfolioShare == null ||
    classTargetPercentage == null ||
    !Number.isFinite(portfolioShare) ||
    !Number.isFinite(classTargetPercentage) ||
    classTargetPercentage === 0
  ) {
    return null;
  }

  return round((portfolioShare / classTargetPercentage) * 100, 2);
};

/**
 * Within-class target at 2dp so {@link computePortfolioPercentage} returns the
 * entered portfolio share at 2dp (inverse + forward can otherwise flip 20.21 → 20.22).
 */
export const computeWithinClassTargetForPortfolioDisplay = (
  portfolioShare: number | null | undefined,
  classTargetPercentage: number | null | undefined,
): number | null => {
  if (
    portfolioShare == null ||
    classTargetPercentage == null ||
    !Number.isFinite(portfolioShare) ||
    !Number.isFinite(classTargetPercentage) ||
    classTargetPercentage === 0
  ) {
    return null;
  }

  const desiredPortfolio = round(portfolioShare, 2);
  const ideal = (desiredPortfolio / classTargetPercentage) * 100;
  const base = round(ideal, 2);
  const candidates = [base, base - 0.01, base + 0.01, base - 0.02, base + 0.02];

  for (const candidate of candidates) {
    if (candidate < 0) continue;
    const displayed = computePortfolioPercentage(
      candidate,
      classTargetPercentage,
    );
    if (displayed === desiredPortfolio) {
      return candidate;
    }
  }

  return base;
};

/** Portfolio % the table shows for `groupIndex` once the class is reconciled. */
const reconciledDisplayAt = (
  withinClassTargets: number[],
  classTargetPercentage: number,
  groupIndex: number,
): number | null =>
  reconcilePortfolioPercentages(
    withinClassTargets.map((withinClassPercentage) => ({
      withinClassPercentage,
      classTargetPercentage,
      groupKey: 'class',
    })),
  )[groupIndex];

const portfolioDisplayMatchesTarget = (
  withinClassTargets: number[],
  groupIndex: number,
  portfolioShare: number,
  classTargetPercentage: number,
): boolean =>
  reconciledDisplayAt(withinClassTargets, classTargetPercentage, groupIndex) ===
  round(portfolioShare, 2);

/**
 * Within-class target for an edited row whose reconciled class display equals
 * the entered share. Per-row inversion alone lets the class residual land on
 * top of the typed value when the row is the class's largest.
 */
const withinClassTargetForReconciledDisplay = (
  withinClassTargets: number[],
  groupIndex: number,
  portfolioShare: number,
  classTargetPercentage: number,
): number | null => {
  const fallback = computeWithinClassTargetForPortfolioDisplay(
    portfolioShare,
    classTargetPercentage,
  );
  if (fallback == null) return null;

  const base = round(
    (round(portfolioShare, 2) / classTargetPercentage) * 100,
    2,
  );
  // One portfolio cent spans about 1 / classTarget of within-class weight.
  const radius = Math.ceil(200 / classTargetPercentage) + 2;

  let best: number | null = null;
  let bestGap = Number.POSITIVE_INFINITY;
  for (let step = -radius; step <= radius; step += 1) {
    const candidate = round(base + step * 0.01, 2);
    if (candidate < 0) continue;
    const trial = withinClassTargets.map((target, index) =>
      index === groupIndex ? candidate : target,
    );
    if (
      !portfolioDisplayMatchesTarget(
        trial,
        groupIndex,
        portfolioShare,
        classTargetPercentage,
      )
    ) {
      continue;
    }
    const gap = Math.abs(sumPortfolioShares(trial) - 100);
    if (gap < bestGap) {
      bestGap = gap;
      best = candidate;
    }
  }

  return best ?? fallback;
};

export const sumPortfolioShares = (portfolioShares: number[]): number =>
  round(
    portfolioShares.reduce(
      (sum, share) => sum + (Number.isFinite(share) ? share : 0),
      0,
    ),
    2,
  );

/** Portfolio shares in the class sum to the branch target within ±1¢ (e.g. 29.00%, not 100%). */
export const classPortfolioSharesMatchBranchTarget = (
  portfolioShares: number[],
  classTargetPercentage: number,
): boolean => {
  if (classTargetPercentage === 0) {
    return false;
  }

  const portfolioTotalCents = Math.round(
    sumPortfolioShares(portfolioShares) * 100,
  );
  const classTargetCents = Math.round(classTargetPercentage * 100);

  return Math.abs(portfolioTotalCents - classTargetCents) <= 1;
};

const distributeCentSlackOnIndices = (
  values: number[],
  indices: number[],
  slackCents: number,
): number[] => {
  const result = [...values];
  if (slackCents === 0 || indices.length === 0) {
    return result;
  }

  const step = slackCents > 0 ? 0.01 : -0.01;
  let remaining = Math.abs(slackCents);
  const ranked = [...indices].sort((a, b) => {
    const fractionA = result[a] * 100 - Math.floor(result[a] * 100 + 1e-9);
    const fractionB = result[b] * 100 - Math.floor(result[b] * 100 + 1e-9);
    return slackCents > 0
      ? fractionB - fractionA || a - b
      : fractionA - fractionB || a - b;
  });

  let pick = 0;
  let guard = 0;
  const maxAttempts = remaining * ranked.length * 2;

  while (remaining > 0 && guard < maxAttempts) {
    const index = ranked[pick % ranked.length];
    const adjusted = round(result[index] + step, 2);
    if (step < 0 && adjusted < 0) {
      pick += 1;
      guard += 1;
      continue;
    }
    result[index] = adjusted;
    remaining -= 1;
    pick += 1;
    guard += 1;
  }

  return result;
};

/**
 * After portfolio-% edits: keep targets for rows whose portfolio % did not change
 * on this commit; update edited row(s) from portfolio share. When the class
 * portfolio sleeve still matches the branch target, distribute rounding slack
 * only across edited row(s) so within-class targets sum to 100.00%.
 */
export const applyWithinClassAfterPortfolioRowEdits = (
  storedWithinClassTargets: number[],
  portfolioShares: number[],
  classTargetPercentage: number,
  editedGroupIndices: number[],
): number[] => {
  const next = [...storedWithinClassTargets];

  if (classTargetPercentage === 0) {
    return next;
  }

  editedGroupIndices.forEach((groupIndex) => {
    const withinClass = withinClassTargetForReconciledDisplay(
      next,
      groupIndex,
      portfolioShares[groupIndex],
      classTargetPercentage,
    );
    if (withinClass != null) {
      next[groupIndex] = withinClass;
    }
  });

  if (
    editedGroupIndices.length === 0 ||
    !classPortfolioSharesMatchBranchTarget(
      portfolioShares,
      classTargetPercentage,
    )
  ) {
    return next;
  }

  const totalCents = Math.round(
    round(
      next.reduce((sum, target) => sum + target, 0),
      2,
    ) * 100,
  );
  const slackCents = 10000 - totalCents;

  if (slackCents === 0) {
    return next;
  }

  // Only correct 1¢ (or one cent per edited row) of target rounding drift — not
  // large drift from a portfolio nudge that moved the sleeve off the branch.
  if (Math.abs(slackCents) > editedGroupIndices.length) {
    return next;
  }

  const beforeSlack = [...next];
  const withSlack = distributeCentSlackOnIndices(
    next,
    editedGroupIndices,
    slackCents,
  );

  const slackPreservesPortfolioDisplay = editedGroupIndices.every(
    (groupIndex) =>
      portfolioDisplayMatchesTarget(
        withSlack,
        groupIndex,
        portfolioShares[groupIndex],
        classTargetPercentage,
      ),
  );

  return slackPreservesPortfolioDisplay ? withSlack : beforeSlack;
};

export const resolvePortfolioPercentage = (
  security: PortfolioPercentageSource,
  classTargetPercentage?: number | null,
  withinClassOverride?: number | null,
  allowEffectiveFallback = true,
): number | null => {
  const withinClass =
    withinClassOverride !== undefined
      ? withinClassOverride
      : security.percentage;

  const derived = computePortfolioPercentage(
    withinClass,
    classTargetPercentage,
  );
  if (derived != null) return derived;

  if (!allowEffectiveFallback) return null;

  return security.effectivePercentage ?? null;
};

export const formatPortfolioPercentage = (
  value: number | null | undefined,
): string => (value == null ? '' : value.toFixed(2));

/** Compare portfolio field drafts at two-decimal precision (blur vs focus snapshot). */
export const portfolioDraftValuesEqual = (a: string, b: string): boolean => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) {
    return a === b;
  }

  return round(na, 2) === round(nb, 2);
};

export const sumTargetPercentages = (
  securities: PortfolioPercentageSource[],
): number => {
  const total = securities.reduce((sum, security) => {
    if (!Number.isFinite(security.percentage)) return sum;

    return sum + Number(security.percentage!.toFixed(2));
  }, 0);

  return round(total, 2);
};

export const sumPortfolioPercentages = (
  securities: PortfolioPercentageSource[],
  classTargetByProductId?: Map<string, number>,
): number => {
  const assetClassGroupedMode = classTargetByProductId != null;

  const total = securities.reduce((sum, security) => {
    const productId = security.financialProduct?.id;
    const classTarget =
      productId != null ? classTargetByProductId?.get(productId) : undefined;
    const withinClass = security.percentage;

    if (
      withinClass != null &&
      classTarget != null &&
      Number.isFinite(withinClass) &&
      Number.isFinite(classTarget)
    ) {
      return sum + (withinClass / 100) * classTarget;
    }

    if (assetClassGroupedMode) {
      return sum;
    }

    const effective = security.effectivePercentage;
    if (effective != null && Number.isFinite(effective)) return sum + effective;

    return sum;
  }, 0);

  return round(total, 2);
};

export const formatTargetPercentage = (
  value: number | null | undefined,
): string => (value == null ? '' : value.toFixed(2));

export const buildClassTargetByProductId = (
  groupedByAssetClass?: Array<{
    securities: PortfolioPercentageSource[];
    branch: { targetPercentage?: number | null };
  }>,
): Map<string, number> => {
  const map = new Map<string, number>();
  groupedByAssetClass?.forEach(({ branch, securities }) => {
    if (branch.targetPercentage == null) return;
    securities.forEach((security) => {
      const productId = security.financialProduct?.id;
      if (productId) map.set(productId, branch.targetPercentage!);
    });
  });
  return map;
};
